import React, {useCallback, useEffect, useState} from "react";
import style from "../../../assets/scss/common/modal/addChildModal.module.scss";
import CloseBtn from "../../../assets/images/modal/close_btn.svg";
import {useAppDispatch, useModal} from "../../../hooks/hooks";
import useValidation from "../../../hooks/useValidation";
import {deleteProfileAsync, editProfileAsync} from "../../../store/profile/profileSlice";
import {
  Address,
  CountryCode,
  CountryFilterValue,
  CountryName,
  InitialProfileInfoDTO,
  NewUserProfile
} from "../../../types/user";
import {ChildNameField} from "./mypage/ChildNameField";
import {ChildAgeField} from "./mypage/ChildAgeField";
import {ChildGenderField} from "./mypage/ChildGenderField";
import {countryCodeMap, getCountryFilterValue} from "../../../util/commonFunctions";
import {ChildAddressField} from "./mypage/ChildAddressField";
import {useTranslation} from "react-i18next";
import {ProfileCommonDescription} from "./mypage/ProfileCommonDescription";


interface ProfileInfo {
  name?: string;
  birthday?: string;
  gender?: string;
  address?: Address;
  idx?: number;
}

interface EditChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileInfo: ProfileInfo | null;
  onProfileAdded: () => void; // 프로필 추가 콜백
  parentAddress?: Address;
  editChildModalData: {
    testCntExists: boolean;
    vouchersAvailable: boolean;
  };
}

const formatBirthday = (birthday: string) => {
  // Convert from 'YYYY-MM-DD' to 'YYYYMMDD'
  return birthday.replace(/-/g, "");
};

const EditChildModal: React.FC<EditChildModalProps> = (
    {
      isOpen,
      onClose,
      profileInfo,
      onProfileAdded,
      parentAddress,
      editChildModalData,
    }
) => {
  const { t } = useTranslation("profileInfo");
  const [isChecked, setIsChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryFilterValue>(CountryFilterValue.NONE);
  const [tempProfileInfo, setTempProfileInfo] = useState<InitialProfileInfoDTO>({
    name: profileInfo?.name || "",
    birthday: profileInfo?.birthday ? formatBirthday(profileInfo.birthday) : "",
    gender: profileInfo?.gender || "",
    address1: profileInfo?.address?.address1 || "",
    address2: profileInfo?.address?.address2 || "",
    city: profileInfo?.address?.city || "",
    state: profileInfo?.address?.state || "",
    address3: profileInfo?.address?.address3 || "",
    country: profileInfo?.address?.country || "",
    postalCode: profileInfo?.address?.postalCode || "",
  });

  const resetProfileInfo = () => {
    setTempProfileInfo({
      name: profileInfo?.name || "",
      birthday: profileInfo?.birthday ? formatBirthday(profileInfo.birthday) : "",
      gender: profileInfo?.gender || "",
      address1: profileInfo?.address?.address1 || "",
      address2: profileInfo?.address?.address2 || "",
      address3: profileInfo?.address?.address3 || "",
      city: profileInfo?.address?.city || "",
      state: profileInfo?.address?.state || "",
      country: selectedCountry,
      postalCode: profileInfo?.address?.postalCode || "",
    });
    const countryName = profileInfo?.address === undefined
        ? CountryFilterValue.NONE
        : getCountryFilterValue(profileInfo.address.country)

    setSelectedCountry(countryName);
    setIsChecked(false); // Reset the checkbox
  };

  const getCountryCode = (selectedCountry: CountryName): CountryCode => {
    return countryCodeMap[selectedCountry];
  };

  useEffect(() => {
    if (profileInfo) {
      // Update tempProfileInfo and selectedCountry
      resetProfileInfo()
    }
    // eslint-disable-next-line
  }, [profileInfo]);

  const {validateName, validateBirthday, validateGender} =
      useValidation();
  const [errors, setErrors] = useState({
    name: "",
    birthday: "",
    gender: "",
  });

  const {openConfirmModal, openAlertModal} = useModal();
  const dispatch = useAppDispatch();

  const validateProfileInfo = (profile: typeof tempProfileInfo) => {
    return {
      name: validateName(profile.name),
      birthday: validateBirthday(profile.birthday),
      gender: validateGender(profile.gender),
    };
  };

  const handleSubmit = () => {
    const validationErrors = validateProfileInfo(tempProfileInfo);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }
    // 프로필 수정
    const data = {
      name: tempProfileInfo.name,
      birthday: `${tempProfileInfo.birthday.slice(0, 4)}-${tempProfileInfo.birthday.slice(
          4,
          6
      )}-${tempProfileInfo.birthday.slice(6)}`,
      gender: tempProfileInfo.gender,
      address: {
        address1: tempProfileInfo.address1,
        address2: tempProfileInfo.address2,
        city: tempProfileInfo.city,
        state: tempProfileInfo.state,
        country: getCountryCode(selectedCountry),
        postalCode: tempProfileInfo.postalCode,
      },
    };

    if (profileInfo?.idx) editProfile(profileInfo?.idx, data).then(onProfileAdded).then(onClose);

    openAlertModal({
      title: t('profileInfo.modal.modify.title'),
      desc: t('profileInfo.modal.modify.complete'),
    });
  };

  const deleteProfile = useCallback(
      async (profileIdx: number) => {
        try {
          const response = await dispatch(deleteProfileAsync(profileIdx)).unwrap();
          console.log("delete profile!", response);
        } catch (error) {
          console.log(error);
        }
      },
      [dispatch]
  );

  const editProfile = useCallback(
      async (profileIdx: number, param: NewUserProfile) => {
        try {
          const response = await dispatch(editProfileAsync({profileIdx, param})).unwrap();
          console.log("Profile edited successfully!", response);
        } catch (error) {
          console.log(error);
        }
      },
      [dispatch]
  );

  const handleRemove = () => {
    // 소속이용권을 보유중인 프로필을 삭제 로직 -> 삭제 불가능
    if (editChildModalData?.vouchersAvailable) {
      openAlertModal({
        title: t('profileInfo.modal.remove.title'),
        desc: `${t('profileInfo.modal.own.desc1')}\n${t('profileInfo.modal.own.desc2')}\n${t('profileInfo.modal.own.desc3')}`,
      });
    } else {
      // 검사 내역이 있는 프로필 삭제 로직 -> 추가 컨펌창
      if (editChildModalData?.testCntExists) {
        openConfirmModal(
            {
              title: t('profileInfo.modal.remove.title'),
              desc: `${t('profileInfo.modal.tester.desc1')}\n${t('profileInfo.modal.tester.desc2')}\n${t('profileInfo.modal.tester.desc3')}`,
            },
            () => {
              if (profileInfo?.idx) deleteProfile(profileInfo?.idx).then(onProfileAdded).then(onClose);
              openAlertModal({
                title: t('profileInfo.modal.remove.title'),
                desc: t('profileInfo.modal.remove.complete'),
              });
            }
        );
      } else {
        openConfirmModal(
            {
              title: t('profileInfo.modal.remove.title'),
              desc: `${t('profileInfo.modal.remove.desc1')}\n${t('profileInfo.modal.remove.desc2')}`,
            },
            () => {
              if (profileInfo?.idx) deleteProfile(profileInfo?.idx).then(onProfileAdded).then(onClose);
              openAlertModal({
                title: t('profileInfo.modal.remove.title'),
                desc: t('profileInfo.modal.remove.complete'),
              });
            }
        );
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
      <div className={style.addChildModalOverlay}>
        <div className={style.addChildInfoModal}>
          <div className={style.closeBtnContainer}>
            <img
                src={CloseBtn}
                alt="closeBtn"
                className={style.closeBtn}
                onClick={() => {
                  openConfirmModal(
                      {
                        title: t('profileInfo.modal.modify.title'),
                        desc: `${t('profileInfo.modal.modify.desc1')}\n${t('profileInfo.modal.modify.desc2')}`,
                      },
                      () => {
                        resetProfileInfo();
                        onClose();
                      }
                  );
                }}
            />
          </div>
          <div className={style.innerModalContainer}>
            <h3 className={style.addChildInfoTitle}>{t('profileInfo.modal.modify.title')}</h3>
            <ProfileCommonDescription />
            <div className={style.innerTextContainer} style={{marginBottom: "20px"}}>
              <ChildNameField profileInfo={tempProfileInfo} setProfileInfo={setTempProfileInfo}
                              errors={errors}/>

              <ChildAgeField profileInfo={tempProfileInfo} setProfileInfo={setTempProfileInfo}
                             errors={errors}/>

              <ChildGenderField
                  profileInfo={tempProfileInfo} setProfileInfo={setTempProfileInfo}
                  errors={errors} setErrors={setErrors}/>

              <ChildAddressField profileInfo={tempProfileInfo} setProfileInfo={setTempProfileInfo}
                                 parentAddress={parentAddress}
                                 selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                                 isChecked={isChecked} setIsChecked={setIsChecked}
              />
            </div>
            <div className={style.profileContainer}>
              <button className={style.removeProfileBtn} onClick={handleRemove}>
                {t('profileInfo.modifyModal.remove')}
              </button>
            </div>
            <button className={style.submitBtn} onClick={handleSubmit}>
              {t('profileInfo.modifyModal.modify')}
            </button>
          </div>
        </div>
      </div>
  );
};

export default EditChildModal;
